import React, { Children, useCallback, useEffect, useLayoutEffect, useRef, useState } from "react"
import { twMerge } from "tailwind-merge"

const SPEED_PX_PER_SEC = 35
const RESUME_DELAY_MS = 1500
const GAP_PX = 24
const CARD_WIDTH_PX = 192
const DEFAULT_CARD_STEP = CARD_WIDTH_PX + GAP_PX

export type VideoReviewsCarouselProps = {
  children: React.ReactNode
  className?: string
  speedPxPerSec?: number
  resumeDelayMs?: number
  cardStepPx?: number
  gapPx?: number
  label?: string
}

function usePrefersReducedMotion(): boolean {
  const [prefers, setPrefers] = useState(false)

  useEffect(() => {
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)")
    setPrefers(mq.matches)
    const handler = () => setPrefers(mq.matches)
    mq.addEventListener("change", handler)
    return () => mq.removeEventListener("change", handler)
  }, [])

  return prefers
}

function isInteractiveTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false
  return Boolean(target.closest("button, a, input, textarea, select, [role='button']"))
}

function normalizeOffset(offset: number, loopWidth: number): number {
  if (loopWidth <= 0) return offset
  let next = offset
  while (next >= loopWidth) next -= loopWidth
  while (next < 0) next += loopWidth
  return next
}

export function VideoReviewsCarousel({
  children,
  className,
  speedPxPerSec = SPEED_PX_PER_SEC,
  resumeDelayMs = RESUME_DELAY_MS,
  cardStepPx = DEFAULT_CARD_STEP,
  gapPx = GAP_PX,
  label = "Video reviews"
}: VideoReviewsCarouselProps): React.ReactElement {
  const prefersReducedMotion = usePrefersReducedMotion()
  const viewportRef = useRef<HTMLDivElement>(null)
  const trackRef = useRef<HTMLDivElement>(null)
  const firstSetRef = useRef<HTMLDivElement>(null)
  const offsetRef = useRef(0)
  const pausedRef = useRef(false)
  const draggingRef = useRef(false)
  const rafRef = useRef<number | null>(null)
  const resumeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const lastFrameTimeRef = useRef<number | null>(null)
  const loopWidthRef = useRef(0)

  const [canScroll, setCanScroll] = useState(false)
  const [isDragging, setIsDragging] = useState(false)

  const childArray = Children.toArray(children)

  const applyTransform = useCallback(() => {
    const track = trackRef.current
    if (!track) return
    track.style.transform = `translate3d(-${offsetRef.current}px, 0, 0)`
  }, [])

  const measure = useCallback(() => {
    const viewport = viewportRef.current
    const firstSet = firstSetRef.current
    if (!viewport || !firstSet) return

    const singleSetWidth = firstSet.scrollWidth
    const viewportWidth = viewport.clientWidth
    const overflow = singleSetWidth > viewportWidth + 1

    setCanScroll(overflow)
    loopWidthRef.current = overflow ? singleSetWidth + gapPx : 0

    if (!overflow) {
      offsetRef.current = 0
      applyTransform()
    } else {
      offsetRef.current = normalizeOffset(offsetRef.current, loopWidthRef.current)
      applyTransform()
    }
  }, [applyTransform, gapPx])

  useLayoutEffect(() => {
    measure()
  }, [measure, childArray.length, canScroll])

  useEffect(() => {
    const viewport = viewportRef.current
    if (!viewport) return

    const observer = new ResizeObserver(() => measure())
    observer.observe(viewport)
    if (firstSetRef.current) observer.observe(firstSetRef.current)

    return () => observer.disconnect()
  }, [measure])

  const clearResumeTimer = useCallback(() => {
    if (resumeTimerRef.current) {
      clearTimeout(resumeTimerRef.current)
      resumeTimerRef.current = null
    }
  }, [])

  const pause = useCallback(() => {
    pausedRef.current = true
    clearResumeTimer()
  }, [clearResumeTimer])

  const scheduleResume = useCallback(() => {
    clearResumeTimer()
    resumeTimerRef.current = setTimeout(() => {
      pausedRef.current = false
      resumeTimerRef.current = null
    }, resumeDelayMs)
  }, [clearResumeTimer, resumeDelayMs])

  const nudge = useCallback(
    (direction: -1 | 1) => {
      pause()
      const loopWidth = loopWidthRef.current
      if (loopWidth > 0) {
        offsetRef.current = normalizeOffset(offsetRef.current + direction * cardStepPx, loopWidth)
      } else {
        offsetRef.current = Math.max(0, offsetRef.current + direction * cardStepPx)
      }
      applyTransform()
      scheduleResume()
    },
    [applyTransform, cardStepPx, pause, scheduleResume]
  )

  useEffect(() => {
    const tick = (now: number) => {
      const last = lastFrameTimeRef.current ?? now
      lastFrameTimeRef.current = now
      const dt = (now - last) / 1000

      const loopWidth = loopWidthRef.current
      if (canScroll && loopWidth > 0 && !prefersReducedMotion && !pausedRef.current && !draggingRef.current) {
        offsetRef.current += speedPxPerSec * dt
        offsetRef.current = normalizeOffset(offsetRef.current, loopWidth)
        applyTransform()
      }

      rafRef.current = requestAnimationFrame(tick)
    }

    rafRef.current = requestAnimationFrame(tick)
    return () => {
      if (rafRef.current !== null) cancelAnimationFrame(rafRef.current)
    }
  }, [applyTransform, canScroll, prefersReducedMotion, speedPxPerSec])

  useEffect(() => () => clearResumeTimer(), [clearResumeTimer])

  const handlePointerDown = (event: React.PointerEvent<HTMLDivElement>) => {
    if (!canScroll || isInteractiveTarget(event.target)) return
    if (event.pointerType === "mouse" && event.button !== 0) return

    draggingRef.current = true
    setIsDragging(true)
    pause()
    event.currentTarget.setPointerCapture(event.pointerId)
  }

  const handlePointerMove = (event: React.PointerEvent<HTMLDivElement>) => {
    if (!draggingRef.current) return

    const loopWidth = loopWidthRef.current
    offsetRef.current -= event.movementX
    if (loopWidth > 0) {
      offsetRef.current = normalizeOffset(offsetRef.current, loopWidth)
    } else {
      offsetRef.current = Math.max(0, offsetRef.current)
    }
    applyTransform()
  }

  const endDrag = (event: React.PointerEvent<HTMLDivElement>) => {
    if (!draggingRef.current) return
    draggingRef.current = false
    setIsDragging(false)
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId)
    }
    scheduleResume()
  }

  const duplicateChildren = childArray.map((child, index) => {
    if (!React.isValidElement(child)) return child
    const key = child.key != null ? `${String(child.key)}-dup` : `dup-${index}`
    return React.cloneElement(child, { key })
  })

  return (
    <div className={twMerge("relative w-full min-w-0 max-w-full self-stretch", className)}>
      {canScroll && (
        <>
          <button
            type="button"
            className="absolute left-2 top-1/2 z-20 flex size-10 -translate-y-1/2 items-center justify-center rounded-full bg-black/50 text-white transition-colors hover:bg-black/70"
            aria-label="Previous video reviews"
            onClick={() => nudge(-1)}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <path
                d="M15 18l-6-6 6-6"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
          <button
            type="button"
            className="absolute right-2 top-1/2 z-20 flex size-10 -translate-y-1/2 items-center justify-center rounded-full bg-black/50 text-white transition-colors hover:bg-black/70"
            aria-label="Next video reviews"
            onClick={() => nudge(1)}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <path
                d="M9 6l6 6-6 6"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
        </>
      )}

      <div
        ref={viewportRef}
        role="region"
        aria-roledescription="carousel"
        aria-label={label}
        className={twMerge(
          "w-full overflow-hidden",
          canScroll && !isDragging && "cursor-grab",
          canScroll && isDragging && "cursor-grabbing"
        )}
        style={{ touchAction: "pan-y" }}
        onMouseEnter={pause}
        onMouseLeave={scheduleResume}
        onFocusCapture={pause}
        onBlurCapture={scheduleResume}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={endDrag}
        onPointerCancel={endDrag}>
        <div
          ref={trackRef}
          className={twMerge("flex w-max flex-row items-stretch will-change-transform", !canScroll && "mx-auto")}
          style={{ gap: gapPx }}>
          <div ref={firstSetRef} className="flex shrink-0 flex-row items-stretch" style={{ gap: gapPx }}>
            {childArray}
          </div>
          {canScroll && (
            <div className="flex shrink-0 flex-row items-stretch" style={{ gap: gapPx }} aria-hidden="true">
              {duplicateChildren}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
